/**
 * Dictation anywhere events (`docs/reference/observability-events.md`).
 * Codes, counts and durations only: never the transcript, the improved text,
 * or a prompt's name or instruction. The applied prompt is described by its
 * kind, its number and the chips its instruction holds (`prompt-chips.ts`).
 */
import { useDictationSettingsStore } from "@/stores/dictation-settings-store";
import { track } from "../observability";
import type {
  DictationAnywhereDeliveryReport,
  DictationAnywherePrompt,
  DictationAnywhereRetryReport,
} from "./dictation-anywhere-native";
import type {
  DictationAnywhereFailure,
  DictationAnywherePhase,
} from "./dictation-anywhere-store";
import { chipsIn } from "./prompt-chips";
import type { NumberedPrompt } from "./polish-prompts";
import type { DictationAnywhereSettings } from "@/stores/dictation-anywhere-settings-store";

/** How long the user spoke and how long the transcript took; null for a
 *  late transcript, whose recording the flow had already given up on. */
export type DictationAnywhereTiming = {
  recording_ms: number;
  transcribe_ms: number;
} | null;

type DictationAnywhereCancelPhase = Extract<
  DictationAnywherePhase,
  "listening" | "transcribing" | "enhancing"
>;

/** The chips in an applied instruction: ids only, never the text. */
const chipIds = (instruction: string | undefined) =>
  instruction ? chipsIn(instruction) : [];

/** The recording began. */
export function trackDictationAnywhereStarted(
  settings: Pick<DictationAnywhereSettings, "defaultPrompt" | "prompts">,
): void {
  track("desktop_voice_dictation_anywhere_started", {
    default_enabled: settings.defaultPrompt.enabled,
    prompt_count: settings.prompts.length,
  });
}

/** Rust answered a delivery. */
export function trackDictationAnywhereDelivered(
  report: DictationAnywhereDeliveryReport,
  details: {
    settings: Pick<DictationAnywhereSettings, "autoPaste">;
    /** What the stop sent: one prompt, or none to deliver as spoken. */
    prompts: DictationAnywherePrompt[];
    /** The numbered prompt the user stopped with; null for Default. */
    promptNumber: number | null;
    timing: DictationAnywhereTiming;
    deliveryMs: number;
  },
): void {
  track("desktop_voice_dictation_anywhere_delivered", {
    delivery: report.delivery,
    delivery_code: report.delivery_code,
    ai: report.ai,
    ai_code: report.ai_code,
    history_saved: report.history_id !== null,
    late: details.timing === null,
    auto_paste: details.settings.autoPaste,
    recording_ms: details.timing?.recording_ms ?? null,
    transcribe_ms: details.timing?.transcribe_ms ?? null,
    delivery_ms: details.deliveryMs,
    transcript_length: report.raw_chars,
    delivered_length: report.delivered_chars,
    image_count: report.images,
    images_pasted: report.images_pasted,
    stop_prompt: details.promptNumber === null ? "default" : "numbered",
    prompt_number: details.promptNumber,
    chip_ids: chipIds(details.prompts[0]?.instruction),
    voice_language_set: useDictationSettingsStore.getState().locale !== null,
  });
}

/** The flow ended without a delivery. */
export function trackDictationAnywhereRefused(
  reason: DictationAnywhereFailure,
): void {
  track("desktop_voice_dictation_anywhere_refused", { reason });
}

/** The user cancelled; in `enhancing` only the AI step is skipped and the
 *  original is still delivered. */
export function trackDictationAnywhereCancelled(
  phase: DictationAnywhereCancelPhase,
): void {
  track("desktop_voice_dictation_anywhere_cancelled", { phase });
}

/** A Retry from Settings › Voice › Recent dictations finished; `report` is
 *  null when the retry itself failed. */
export function trackDictationAnywhereRetried(
  report: DictationAnywhereRetryReport | null,
  durationMs: number,
  prompt: NumberedPrompt | DictationAnywherePrompt,
): void {
  track("desktop_voice_dictation_anywhere_retried", {
    result: report?.ai ?? "failed",
    ai_code: report?.ai_code ?? null,
    copied: report?.copied ?? false,
    duration_ms: durationMs,
    stop_prompt: "number" in prompt ? "numbered" : "default",
    chip_ids: chipIds(prompt.instruction),
  });
}
